/*
 * 废弃标注：本模块为 V2 operationControl，已业务废弃且不再维护，禁止在其上开发新功能。
 * 当前在用版本为 V2.1：apps/portal_web/src/features/operation-control/。
 * V2.1 设计文档位于：apps/portal_web/src/features/operation-control/docs/。
 * 本文件仅因旧前端路由尚未退役而保留，待路由退役时应整体删除。
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Divider, Dropdown, Empty, Form, Input, List, Modal, Popconfirm, Select, Space, Spin, Tag, Typography, message } from "antd";
import { DeleteOutlined, PlusOutlined, ReloadOutlined, SettingOutlined, SyncOutlined } from "@ant-design/icons";
import AircraftSelector from "../../components/common/AircraftSelector";
import {
    GridShell,
    LightCell,
    useColumnBlocks,
    useEditBuffer,
    useEditBufferCellStatus,
    useEditBufferCellValue,
    useGridColumnState,
    useGridSelection,
    useGridSort,
} from "../../components/common/GridCore";
import operationApi from "../../api/v3/operationApi";
import userPreferenceApi from "../../api/v3/userPreferenceApi";
import { useAuth } from "../../hooks/useAuth";
import {
    DEFAULT_OPERATION_DATE_RANGE_KEY,
    OPERATION_CORE_COLUMNS,
    OPERATION_SORTABLE_COLUMNS,
    buildOperationCellKey,
    buildOperationColumnBlocks,
    buildOperationDateRangeOptions,
    getBlockByCategory,
    getDateRangeOption,
    getFieldLabel,
    getPrimaryOperationItem,
    projectOperationLegRows,
    readOperationItemField,
    resolveOperationFieldConfig,
} from "./gridModel";
import "./OperationControlPage.css";

const { Text, Title } = Typography;

const EMPTY_SUMMARY = Object.freeze({
    total_legs: 0,
    signal_counts: { mail: 0, review: 0, alert: 0 },
});
const EMPTY_SIGNALS = Object.freeze([]);

const ROW_HEADER_COLUMN_KEY = "__row_header__";
const OPERATION_GRID_PREFERENCE_SCOPE = "operationControl.grid";
const OPERATION_FIELD_CONFIG_SCOPE = "operationControl.fieldConfig";
const OPERATION_FIELD_CONFIG_PREFERENCE_KEY = "fieldConfig";
const OPERATION_GRID_COLUMN_WIDTH_CONSTRAINTS = Object.freeze({ minWidth: 8, maxWidth: 400 });
const OPERATION_FOCUS_CELL_CLASS = "operation-control-light-cell-focused";
const OPERATION_ATTRIBUTE_FIELD_KEY_PATTERN = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)*$/;
const FIXED_OPERATION_FIELDS = Object.freeze([
    { key: "status", label: "Status" },
    { key: "remarks", label: "Remarks" },
]);
const OPERATION_GRID_PREFERENCE_KEYS = Object.freeze({
    columnWidth: "columnWidth",
    sort: "sort",
});

const ARROW_KEY_DELTAS = Object.freeze({
    ArrowUp: { row: -1, col: 0 },
    ArrowDown: { row: 1, col: 0 },
    ArrowLeft: { row: 0, col: -1 },
    ArrowRight: { row: 0, col: 1 },
});

const SELECTION_TYPE_LABELS = Object.freeze({
    single: "单格",
    range: "范围",
    multi: "多选",
    row: "整行",
    column: "整列",
    block: "列块",
});

const EDIT_BUFFER_STATUS_CLASS_NAMES = Object.freeze({
    dirty: "edit-buffer-dirty",
    submitting: "edit-buffer-submitting",
    error: "edit-buffer-error",
});

function buildOperationColumnKey(category, fieldKey) {
    return `${category}.${fieldKey}`;
}

function parseOperationCellKey(cellKey) {
    if (typeof cellKey !== "string") return null;
    const [rawLegId, category, ...fieldKeyParts] = cellKey.split(":");
    const fieldKey = fieldKeyParts.join(":");
    const legId = Number(rawLegId);
    if (!Number.isInteger(legId) || legId <= 0 || !category || !fieldKey) return null;
    return { legId, category, fieldKey };
}

function buildRowIndexByLegId(rows = []) {
    const rowIndexByLegId = new Map();
    rows.forEach((row, index) => {
        if (row?.leg_id !== null && row?.leg_id !== undefined) {
            rowIndexByLegId.set(String(row.leg_id), index);
        }
    });
    return rowIndexByLegId;
}

function applyEditBufferCellToDisplayRows(currentRows, serverRows, rowIndexByLegId, editBufferStore, cellKey) {
    const parsedCellKey = parseOperationCellKey(cellKey);
    if (!parsedCellKey) return currentRows;

    const rowIndex = rowIndexByLegId.get(String(parsedCellKey.legId));
    if (!Number.isInteger(rowIndex)) return currentRows;

    const serverRow = serverRows[rowIndex];
    const currentRow = currentRows[rowIndex] || serverRow;
    if (!serverRow || !currentRow) return currentRows;
    const sourceRow = String(currentRow.leg_id) === String(serverRow.leg_id) ? currentRow : serverRow;

    const columnKey = buildOperationColumnKey(parsedCellKey.category, parsedCellKey.fieldKey);
    const serverValue = serverRow[columnKey];
    const displayValue = editBufferStore.getCellValue(cellKey, serverValue);
    if (Object.is(sourceRow[columnKey], displayValue)) return currentRows;

    const nextRows = currentRows === serverRows || currentRows.length !== serverRows.length ? [...serverRows] : [...currentRows];
    nextRows[rowIndex] = { ...sourceRow, [columnKey]: displayValue };
    return nextRows;
}

function applyPendingEditsToDisplayRows(serverRows, pendingEdits, rowIndexByLegId, editBufferStore) {
    if (!pendingEdits?.size) return serverRows;

    let nextRows = serverRows;
    pendingEdits.forEach((_, cellKey) => {
        nextRows = applyEditBufferCellToDisplayRows(nextRows, serverRows, rowIndexByLegId, editBufferStore, cellKey);
    });
    return nextRows;
}

function buildVisibleOperationCellKeys(rows = [], columnBlocks = []) {
    const cellKeys = [];
    rows.forEach((row) => {
        const legId = row?.leg_id;
        if (legId === null || legId === undefined) return;
        columnBlocks.forEach((block) => {
            const category = block.category || block.transactionType;
            if (!category) return;
            (block.columns || []).forEach((field) => {
                if (!field?.key) return;
                cellKeys.push(buildOperationCellKey(legId, category, field.key));
            });
        });
    });
    return cellKeys;
}

function getEditBufferCellClassName(status) {
    return EDIT_BUFFER_STATUS_CLASS_NAMES[status] || undefined;
}

function OperationEditBufferCell({
    editBufferStore,
    cellKey,
    serverValue,
    signals,
    rowId,
    rowIndex,
    columnKey,
    columnIndex,
    focusClassName,
}) {
    const value = useEditBufferCellValue(editBufferStore, cellKey, serverValue);
    const status = useEditBufferCellStatus(editBufferStore, cellKey);
    const errorEntry = status === "error" ? editBufferStore?.getPendingEdits?.().get(cellKey) : null;
    const errorTitle = status === "error" ? errorEntry?.errorMessage || errorEntry?.errorCode || "提交失败" : undefined;

    return (
        <LightCell
            value={value}
            signals={signals}
            className={getEditBufferCellClassName(status)}
            title={errorTitle}
            rowId={rowId}
            rowIndex={rowIndex}
            columnKey={columnKey}
            columnIndex={columnIndex}
            focusClassName={focusClassName}
        />
    );
}

const MemoOperationEditBufferCell = React.memo(OperationEditBufferCell, (previous, next) => (
    previous.editBufferStore === next.editBufferStore
    && previous.cellKey === next.cellKey
    && Object.is(previous.serverValue, next.serverValue)
    && previous.signals === next.signals
    && previous.rowId === next.rowId
    && previous.rowIndex === next.rowIndex
    && previous.columnKey === next.columnKey
    && previous.columnIndex === next.columnIndex
    && previous.focusClassName === next.focusClassName
));

function OperationEditBufferToolbar({ pendingCount, submitting, submittingCount, canUndo, canRedo, onSubmit, onDiscardAll }) {
    if (!pendingCount && !submitting) return null;

    return (
        <div className="operation-control-edit-buffer-bar">
            <Space size={10} wrap>
                {submitting ? (
                    <>
                        <Spin size="small" />
                        <Text strong>正在提交 {submittingCount || pendingCount} 项...</Text>
                    </>
                ) : (
                    <>
                        <Text strong>{pendingCount} 项待提交</Text>
                        <Text type="secondary">
                            {canUndo ? `Ctrl+Z 撤销上一步${canRedo ? "，Ctrl+Y 重做" : ""}` : "可修正失败项后重提，或撤销所有编辑"}
                        </Text>
                    </>
                )}
            </Space>
            <Space size={8} wrap>
                <Button type="primary" size="small" loading={submitting} disabled={!pendingCount || submitting} onClick={onSubmit}>
                    提交
                </Button>
                <Button size="small" disabled={!pendingCount || submitting} onClick={onDiscardAll}>
                    撤销所有编辑
                </Button>
            </Space>
        </div>
    );
}

function getColumnIndex(column, columns) {
    if (Number.isInteger(column?.idx)) return column.idx;
    return columns.findIndex((candidate) => candidate.key === column?.key);
}

function isNavigationColumn(column) {
    return column?.isRowHeader !== true && column?.excludeFromSelection !== true;
}

function getNextNavigationColumnIndex(columns, colIdx, step) {
    for (let nextColIdx = colIdx + step; nextColIdx >= 0 && nextColIdx < columns.length; nextColIdx += step) {
        if (isNavigationColumn(columns[nextColIdx])) return nextColIdx;
    }
    return null;
}

function getShiftArrowTarget(rowIdx, colIdx, key, rowCount, columns) {
    const delta = ARROW_KEY_DELTAS[key];
    if (!delta) return null;

    if (delta.row !== 0) {
        const nextRowIdx = rowIdx + delta.row;
        if (nextRowIdx < 0 || nextRowIdx >= rowCount) return null;
        return { rowIdx: nextRowIdx, colIdx };
    }

    const nextColIdx = getNextNavigationColumnIndex(columns, colIdx, delta.col);
    return nextColIdx === null ? null : { rowIdx, colIdx: nextColIdx };
}

function buildFocusContext(selection, columnBlocks) {
    const focus = selection.focus;
    if (!focus) return "未选中单元格。选择工作表中的任一格后，此处显示焦点坐标和字段上下文。";

    const row = focus.row;
    if (!row) return `Row ${focus.rowIdx + 1}, Column ${focus.colIdx + 1}`;

    const category = focus.category || focus.transactionType;
    if (!category) {
        return `${row.date} / ${row.aircraft} / ${row.flight || row.leg} / ${focus.columnKey}: ${focus.value || "-"}`;
    }

    const block = getBlockByCategory(category, columnBlocks);
    const fieldLabel = getFieldLabel(block, focus.fieldKey);
    return `${row.date} / ${row.aircraft} / ${row.flight || row.leg} / ${block?.label || category}.${fieldLabel}: ${focus.value || "-"}`;
}

function renderOperationLightCell(row, columnKey, value, placeholder = "-", rowIndex, columnIndex) {
    return (
        <LightCell
            value={value}
            placeholder={placeholder}
            rowId={row.row_id}
            rowIndex={rowIndex}
            columnKey={columnKey}
            columnIndex={columnIndex}
            focusClassName={OPERATION_FOCUS_CELL_CLASS}
        />
    );
}

function flattenLeafColumns(columns = []) {
    return columns.flatMap((column) => (Array.isArray(column.children) ? flattenLeafColumns(column.children) : [column]));
}

function createUserPreferenceAdapter(scope, key) {
    return {
        load: () => userPreferenceApi.getPreference({ scope, key }),
        save: (value) => userPreferenceApi.upsertPreference({ scope, key, value }),
    };
}

function isEditableItemColumn(column) {
    return Boolean(column?.editable === true && column.transactionType && column.fieldKey);
}

function normalizeCommitValue(column, value) {
    if (column?.normalize) return column.normalize(value);
    if (column?.fieldKey === "remarks") return value === null || value === undefined ? "" : String(value);
    return value;
}

function areCellValuesEqual(left, right) {
    return (left ?? "") === (right ?? "");
}

function applyOperationItemFields(item, fields) {
    if (!item) return item;

    let nextAttributes = item.attributes && typeof item.attributes === "object" ? item.attributes : {};
    const nextItem = { ...item };

    Object.entries(fields).forEach(([fieldKey, value]) => {
        if (fieldKey === "status") {
            nextItem.status = value ?? "";
            return;
        }
        if (fieldKey === "remarks") {
            nextItem.remarks = value;
            return;
        }
        if (fieldKey === "order_id") {
            nextItem.order_id = value;
            return;
        }
        if (nextAttributes === item.attributes) nextAttributes = { ...nextAttributes };
        nextAttributes[fieldKey] = value;
    });

    if (nextAttributes !== item.attributes) nextItem.attributes = nextAttributes;
    return nextItem;
}

function mergeServerOperationItem(item, fields, serverItem) {
    const optimisticItem = applyOperationItemFields(item, fields);
    if (!serverItem || typeof serverItem !== "object") return optimisticItem;

    const serverAttributes = serverItem.attributes && typeof serverItem.attributes === "object" ? serverItem.attributes : {};
    const nextItem = {
        ...optimisticItem,
        ...serverItem,
        attributes: {
            ...(optimisticItem?.attributes || {}),
            ...serverAttributes,
        },
    };

    Object.entries(fields).forEach(([fieldKey, value]) => {
        if (fieldKey === "status" && nextItem.status === undefined) {
            nextItem.status = value ?? "";
        } else if (fieldKey === "remarks" && nextItem.remarks === undefined) {
            nextItem.remarks = value;
        } else if (fieldKey !== "status" && fieldKey !== "remarks" && nextItem.attributes[fieldKey] === undefined) {
            nextItem.attributes[fieldKey] = value;
        }
    });

    return nextItem;
}

function updatePayloadOperationItem(payload, legId, category, itemId, updater) {
    let changed = false;
    const items = (payload.items || []).map((item) => {
        if (String(item.leg_id) !== String(legId)) return item;

        const groupedItems = item.items || {};
        const categoryItems = Array.isArray(groupedItems[category]) ? groupedItems[category] : [];
        let itemChanged = false;
        const nextCategoryItems = categoryItems.map((operationItem) => {
            if (String(operationItem.id) !== String(itemId)) return operationItem;

            const nextOperationItem = updater(operationItem, item);
            if (nextOperationItem === operationItem) return operationItem;

            itemChanged = true;
            return nextOperationItem;
        });

        if (!itemChanged) return item;

        changed = true;
        return {
            ...item,
            items: {
                ...groupedItems,
                [category]: nextCategoryItems,
            },
        };
    });

    return changed ? { ...payload, items } : payload;
}

function insertPayloadOperationItem(payload, legId, category, createdItem) {
    if (!createdItem) return payload;

    let changed = false;
    const items = (payload.items || []).map((item) => {
        if (String(item.leg_id) !== String(legId)) return item;

        const groupedItems = item.items || {};
        const categoryItems = Array.isArray(groupedItems[category]) ? groupedItems[category] : [];
        const existingIndex = categoryItems.findIndex((operationItem) => String(operationItem.id) === String(createdItem.id));
        const nextCategoryItems = existingIndex >= 0
            ? categoryItems.map((operationItem, index) => (index === existingIndex ? createdItem : operationItem))
            : [...categoryItems, createdItem];

        changed = true;
        return {
            ...item,
            items: {
                ...groupedItems,
                [category]: nextCategoryItems,
            },
        };
    });

    return changed ? { ...payload, items } : payload;
}

function buildOperationAggregateByLegId(payloadItems = []) {
    return payloadItems.reduce((lookup, item) => {
        if (item?.leg_id !== null && item?.leg_id !== undefined) {
            lookup.set(String(item.leg_id), item);
        }
        return lookup;
    }, new Map());
}

function buildEditBufferBatchGroups(pendingEdits, payloadItems = []) {
    const aggregateByLegId = buildOperationAggregateByLegId(payloadItems);
    const groupsByKey = new Map();

    pendingEdits.forEach((entry, cellKey) => {
        const parsedCellKey = parseOperationCellKey(cellKey);
        if (!parsedCellKey) return;

        const groupKey = `${parsedCellKey.legId}:${parsedCellKey.category}`;
        const group = groupsByKey.get(groupKey) || {
            legId: parsedCellKey.legId,
            category: parsedCellKey.category,
            changes: {},
            cellKeys: [],
            fieldKeys: [],
            operation: null,
            existingItem: null,
        };

        group.changes[parsedCellKey.fieldKey] = entry.value;
        group.cellKeys.push(cellKey);
        group.fieldKeys.push(parsedCellKey.fieldKey);
        groupsByKey.set(groupKey, group);
    });

    return Array.from(groupsByKey.values()).map((group) => {
        const aggregateItem = aggregateByLegId.get(String(group.legId));
        const existingItem = getPrimaryOperationItem(aggregateItem?.items, group.category);
        const hasExistingItem = existingItem?.id !== null && existingItem?.id !== undefined;

        return {
            ...group,
            existingItem,
            operation: hasExistingItem
                ? {
                      op_type: "update",
                      item_id: existingItem.id,
                      changes: group.changes,
                      expected_lock_version: existingItem.lock_version ?? 0,
                  }
                : {
                      op_type: "create",
                      leg_id: group.legId,
                      category: group.category,
                      changes: group.changes,
                      expected_lock_version: 0,
                  },
        };
    });
}

function mapBatchSubmitResults(batchGroups, serverResults) {
    const normalizedResults = Array.isArray(serverResults) ? serverResults : [];
    const mappedResults = [];

    batchGroups.forEach((group, groupIndex) => {
        const result = normalizedResults[groupIndex] || {
            success: false,
            error_code: "OPERATION_ERROR",
            error_message: "批量端点未返回该操作结果",
        };
        const resultFieldKeys = Array.isArray(result.field_keys) && result.field_keys.length > 0
            ? result.field_keys
            : group.fieldKeys;

        resultFieldKeys.forEach((fieldKey) => {
            mappedResults.push({
                cellKey: buildOperationCellKey(group.legId, group.category, fieldKey),
                success: result.success === true,
                errorCode: result.error_code,
                errorMessage: result.error_message,
            });
        });
    });

    return mappedResults;
}

function buildBatchCreatedOperationItem(group, result) {
    const createdItem = {
        id: result.item_id,
        leg_id: group.legId,
        category: group.category,
        status: "",
        remarks: null,
        attributes: {},
        order_id: null,
        lock_version: result.new_lock_version ?? 0,
    };
    return applyOperationItemFields(createdItem, group.changes);
}

function applyBatchSuccessResultsToPayload(payload, batchGroups, serverResults) {
    let nextPayload = payload;
    const normalizedResults = Array.isArray(serverResults) ? serverResults : [];

    batchGroups.forEach((group, groupIndex) => {
        const result = normalizedResults[groupIndex];
        if (result?.success !== true) return;

        const itemId = result.item_id ?? group.existingItem?.id;
        if (itemId === null || itemId === undefined) return;

        if (group.operation?.op_type === "create") {
            nextPayload = insertPayloadOperationItem(
                nextPayload,
                group.legId,
                group.category,
                buildBatchCreatedOperationItem(group, { ...result, item_id: itemId }),
            );
            return;
        }

        nextPayload = updatePayloadOperationItem(
            nextPayload,
            group.legId,
            group.category,
            itemId,
            (operationItem) => mergeServerOperationItem(operationItem, group.changes, {
                id: itemId,
                lock_version: result.new_lock_version ?? operationItem?.lock_version,
            }),
        );
    });

    return nextPayload;
}

function getSubmitFailureMessage(error) {
    return error?.response?.data?.message || error?.message || "批量提交失败";
}

function serializeOperationFieldConfig(config) {
    return Object.entries(resolveOperationFieldConfig(config)).reduce((serialized, [category, categoryConfig]) => {
        serialized[category] = {
            fields: (categoryConfig.fields || []).map((field) => ({
                key: field.key,
                label: field.label || field.key,
                editable: field.editable !== false,
            })),
        };
        return serialized;
    }, {});
}

function OperationControlPage() {
    const { hasPermission } = useAuth();
    const [fieldForm] = Form.useForm();
    const fixedAreaRef = useRef(null);
    const gridShellRef = useRef(null);
    const [selectedAircraft, setSelectedAircraft] = useState(null);
    const [dateRangeKey, setDateRangeKey] = useState(DEFAULT_OPERATION_DATE_RANGE_KEY);
    const [payload, setPayload] = useState({ items: [], summary: EMPTY_SUMMARY });
    const [fieldConfig, setFieldConfig] = useState(() => resolveOperationFieldConfig());
    const [fieldConfigLoaded, setFieldConfigLoaded] = useState(false);
    const [fieldManager, setFieldManager] = useState({ open: false, category: null, fields: [], saving: false });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [reloadToken, setReloadToken] = useState(0);
    const [editBufferSubmitting, setEditBufferSubmitting] = useState(false);
    const [editBufferSubmittingCount, setEditBufferSubmittingCount] = useState(0);
    const editBuffer = useEditBuffer();
    const editBufferStore = editBuffer.store;
    const columnBlocks = useMemo(() => buildOperationColumnBlocks(fieldConfig), [fieldConfig]);
    const rowsProjectionRef = useRef({ items: [], rows: [] });
    const serverRows = useMemo(() => {
        const nextRows = projectOperationLegRows(payload.items, rowsProjectionRef.current, columnBlocks);
        rowsProjectionRef.current = { items: payload.items, rows: nextRows, columnBlocks };
        return nextRows;
    }, [columnBlocks, payload.items]);
    const rowIndexByLegId = useMemo(() => buildRowIndexByLegId(serverRows), [serverRows]);
    const serverRowsRef = useRef(serverRows);
    const rowIndexByLegIdRef = useRef(rowIndexByLegId);
    const [rows, setRows] = useState(() => serverRows);
    const visibleOperationCellKeys = useMemo(
        () => buildVisibleOperationCellKeys(serverRows, columnBlocks),
        [columnBlocks, serverRows],
    );
    const canEditItems = hasPermission("flight_operation:update");
    const gridShapeRef = useRef({ rows: [], columns: [] });
    const skipNextSelectedCellChangeRef = useRef(false);

    serverRowsRef.current = serverRows;
    rowIndexByLegIdRef.current = rowIndexByLegId;

    useLayoutEffect(() => {
        const nextRows = applyPendingEditsToDisplayRows(
            serverRows,
            editBufferStore.getPendingEdits(),
            rowIndexByLegId,
            editBufferStore,
        );
        setRows((currentRows) => (currentRows === nextRows ? currentRows : nextRows));
    }, [editBufferStore, rowIndexByLegId, serverRows]);

    useEffect(() => {
        const unsubscribeCallbacks = visibleOperationCellKeys.map((cellKey) => editBufferStore.subscribeCell(cellKey, () => {
            setRows((currentRows) => applyEditBufferCellToDisplayRows(
                currentRows,
                serverRowsRef.current,
                rowIndexByLegIdRef.current,
                editBufferStore,
                cellKey,
            ));
        }));

        return () => {
            unsubscribeCallbacks.forEach((unsubscribe) => unsubscribe());
        };
    }, [editBufferStore, visibleOperationCellKeys]);

    const getOperationRowKey = useCallback((row) => row.row_id, []);
    const getSelectionRows = useCallback(() => gridShapeRef.current.rows, []);
    const getSelectionColumns = useCallback(() => gridShapeRef.current.columns, []);
    const getSelectionCellMetadata = useCallback((rowIdx, colIdx) => {
        const row = gridShapeRef.current.rows[rowIdx];
        const column = gridShapeRef.current.columns[colIdx];
        if (!row || !column) return {};

        const category = column.category || column.transactionType;
        const operationItem = category ? getPrimaryOperationItem(row.items, category) : null;
        const block = category ? getBlockByCategory(category, columnBlocks) : null;
        const cellKey = category ? buildOperationCellKey(row.leg_id, category, column.fieldKey) : undefined;
        const serverValue = category ? readOperationItemField(operationItem, column.fieldKey) : row[column.key];
        const value = cellKey ? editBufferStore.getCellValue(cellKey, serverValue) : serverValue;

        return {
            rowId: row.row_id,
            columnKey: column.key,
            row,
            category,
            transactionType: category,
            blockId: block?.blockId ?? column.blockId,
            fieldKey: column.fieldKey,
            itemId: operationItem?.id,
            lockVersion: operationItem?.lock_version,
            cellKey,
            value,
        };
    }, [columnBlocks, editBufferStore]);
    const {
        selection,
        setFocus,
        extendRange,
        toggleCell,
        selectRow,
        selectColumn,
        selectBlock,
        clearSelection,
    } = useGridSelection({
        getRows: getSelectionRows,
        getColumns: getSelectionColumns,
        getCellMetadata: getSelectionCellMetadata,
    });
    const skipNextNativeSelectionChange = useCallback(() => {
        skipNextSelectedCellChangeRef.current = true;
        if (typeof window === "undefined") return;
        window.setTimeout(() => {
            skipNextSelectedCellChangeRef.current = false;
        }, 0);
    }, []);
    const dateRangeOptions = useMemo(() => buildOperationDateRangeOptions(), []);
    const selectedDateRange = useMemo(() => getDateRangeOption(dateRangeKey, dateRangeOptions), [dateRangeKey, dateRangeOptions]);
    const columnWidthPreferenceAdapter = useMemo(
        () => createUserPreferenceAdapter(OPERATION_GRID_PREFERENCE_SCOPE, OPERATION_GRID_PREFERENCE_KEYS.columnWidth),
        [],
    );
    const sortPreferenceAdapter = useMemo(
        () => createUserPreferenceAdapter(OPERATION_GRID_PREFERENCE_SCOPE, OPERATION_GRID_PREFERENCE_KEYS.sort),
        [],
    );
    const handleSortChange = useCallback(() => {
        setReloadToken((current) => current + 1);
    }, []);
    const gridSort = useGridSort({
        sortableColumns: OPERATION_SORTABLE_COLUMNS,
        persistence: sortPreferenceAdapter,
        onSortChange: handleSortChange,
    });
    const operationSortBy = gridSort.state.by;
    const operationSortOrder = gridSort.state.order;
    const operationSortLoaded = gridSort.isLoaded;

    useEffect(() => {
        let cancelled = false;

        async function loadFieldConfig() {
            try {
                const preferencePayload = await userPreferenceApi.listPreferences({ scope: OPERATION_FIELD_CONFIG_SCOPE });
                if (!cancelled) setFieldConfig(resolveOperationFieldConfig(preferencePayload));
            } catch (fieldConfigError) {
                console.warn("加载 operationControl 字段配置失败，使用默认配置", fieldConfigError);
                if (!cancelled) setFieldConfig(resolveOperationFieldConfig());
            } finally {
                if (!cancelled) setFieldConfigLoaded(true);
            }
        }

        loadFieldConfig();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!selectedAircraft || !operationSortLoaded) return undefined;
        let cancelled = false;

        async function loadLegs() {
            setLoading(true);
            setError("");
            try {
                const nextPayload = await operationApi.listLegs({
                    aircraft: selectedAircraft,
                    date_from: selectedDateRange.date_from,
                    date_to: selectedDateRange.date_to,
                    sort_by: operationSortBy || undefined,
                    sort_order: operationSortBy ? operationSortOrder : undefined,
                });
                if (!cancelled) {
                    setPayload({
                        items: nextPayload?.items || [],
                        summary: nextPayload?.summary || EMPTY_SUMMARY,
                    });
                    clearSelection();
                }
            } catch (loadError) {
                console.error("加载 operation legs 失败", loadError);
                if (!cancelled) {
                    setPayload({ items: [], summary: EMPTY_SUMMARY });
                    setError("运行控制数据暂时无法加载");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        loadLegs();
        return () => {
            cancelled = true;
        };
    }, [
        clearSelection,
        operationSortBy,
        operationSortLoaded,
        operationSortOrder,
        reloadToken,
        selectedAircraft,
        selectedDateRange.date_from,
        selectedDateRange.date_to,
    ]);

    const focusContext = useMemo(() => buildFocusContext(selection, columnBlocks), [columnBlocks, selection]);
    const signalCounts = payload.summary?.signal_counts || EMPTY_SUMMARY.signal_counts;
    const selectionCellCount = selection.cells?.size ?? 0;
    const selectionTypeLabel = SELECTION_TYPE_LABELS[selection.type] || "选区";

    const rowHeaderColumn = useMemo(
        () => ({
            key: ROW_HEADER_COLUMN_KEY,
            name: "#",
            width: 42,
            minWidth: 36,
            maxWidth: 48,
            editable: false,
            resizable: false,
            sortable: false,
            isRowHeader: true,
            excludeFromSelection: true,
            headerCellClass: "grid-core-row-header-cell",
            cellClass: "grid-core-row-header-cell",
            renderCell: ({ rowIdx }) => <span className="grid-core-row-header-index">{rowIdx + 1}</span>,
        }),
        [],
    );

    const frozenColumns = useMemo(
        () => [
            {
                key: "date",
                name: "Date",
                width: 82,
                minWidth: 76,
                maxWidth: 96,
                editable: false,
                renderCell: ({ row, rowIdx, column }) => renderOperationLightCell(row, "date", row.date, "-", rowIdx, column.idx),
            },
            {
                key: "aircraft",
                name: "A/C REG",
                width: 96,
                minWidth: 88,
                maxWidth: 120,
                editable: false,
                renderCell: ({ row, rowIdx, column }) => renderOperationLightCell(row, "aircraft", row.aircraft, "-", rowIdx, column.idx),
            },
        ],
        [],
    );

    const coreColumns = useMemo(
        () =>
            OPERATION_CORE_COLUMNS.map((column) => ({
                key: column.key,
                name: column.label,
                width: column.width,
                minWidth: column.minWidth || column.width,
                maxWidth: column.maxWidth,
                frozen: false,
                editable: column.editable === true,
                cellType: column.cellType,
                sortable: Boolean(column.sortBy),
                sortBy: column.sortBy,
                headerCellClass: "operation-control-core-header-cell",
                cellClass: "operation-control-core-cell",
                renderCell: ({ row, rowIdx, column: gridColumn }) => renderOperationLightCell(row, column.key, row[column.key], column.placeholder, rowIdx, gridColumn.idx),
            })),
        [],
    );

    const leadingColumns = useMemo(() => [rowHeaderColumn, ...frozenColumns, ...coreColumns], [coreColumns, frozenColumns, rowHeaderColumn]);

    const renderBlockCell = useCallback(
        ({ row, rowIdx, column }, { block, field, columnKey }) => {
            const category = block.category || block.transactionType;
            const operationItem = getPrimaryOperationItem(row.items, category);
            const serverValue = readOperationItemField(operationItem, field.key);
            const cellKey = buildOperationCellKey(row.leg_id, category, field.key);
            const signals = field.signalAware ? operationItem?.signals ?? EMPTY_SIGNALS : EMPTY_SIGNALS;
            return (
                <MemoOperationEditBufferCell
                    editBufferStore={editBufferStore}
                    cellKey={cellKey}
                    serverValue={serverValue}
                    signals={signals}
                    rowId={row.row_id}
                    rowIndex={rowIdx}
                    columnKey={columnKey}
                    columnIndex={column.idx}
                    focusClassName={OPERATION_FOCUS_CELL_CLASS}
                />
            );
        },
        [editBufferStore],
    );

    const handleBlockHeaderClick = useCallback((block) => {
        selectBlock(block.blockId);
    }, [selectBlock]);

    const openFieldManager = useCallback(
        (block) => {
            const category = block.category || block.transactionType;
            if (!category) return;

            const categoryConfig = fieldConfig[category] || {};
            setFieldManager({
                open: true,
                category,
                fields: (categoryConfig.fields || []).map((field) => ({ ...field })),
                saving: false,
            });
            fieldForm.resetFields();
        },
        [fieldConfig, fieldForm],
    );

    const closeFieldManager = useCallback(() => {
        setFieldManager((current) => (current.saving ? current : { open: false, category: null, fields: [], saving: false }));
        fieldForm.resetFields();
    }, [fieldForm]);

    const handleAddField = useCallback(
        async () => {
            try {
                const values = await fieldForm.validateFields();
                const key = String(values.key || "").trim();
                const label = String(values.label || "").trim() || key;
                if (FIXED_OPERATION_FIELDS.some((field) => field.key === key)) {
                    fieldForm.setFields([{ name: "key", errors: ["字段键不能使用固定字段"] }]);
                    return;
                }
                if (fieldManager.fields.some((field) => field.key === key)) {
                    fieldForm.setFields([{ name: "key", errors: ["字段键已存在"] }]);
                    return;
                }

                setFieldManager((current) => ({
                    ...current,
                    fields: [...current.fields, { key, label, editable: true }],
                }));
                fieldForm.resetFields();
            } catch (validationError) {
                if (!validationError?.errorFields) throw validationError;
            }
        },
        [fieldForm, fieldManager.fields],
    );

    const handleRemoveField = useCallback((fieldKey) => {
        setFieldManager((current) => ({
            ...current,
            fields: current.fields.filter((field) => field.key !== fieldKey),
        }));
    }, []);

    const handleSaveFieldConfig = useCallback(
        async () => {
            const { category, fields } = fieldManager;
            if (!category) return;

            const nextConfig = resolveOperationFieldConfig({
                ...fieldConfig,
                [category]: {
                    ...(fieldConfig[category] || {}),
                    fields,
                },
            });

            setFieldManager((current) => ({ ...current, saving: true }));
            try {
                await userPreferenceApi.upsertPreference({
                    scope: OPERATION_FIELD_CONFIG_SCOPE,
                    key: OPERATION_FIELD_CONFIG_PREFERENCE_KEY,
                    value: serializeOperationFieldConfig(nextConfig),
                });
                setFieldConfig(nextConfig);
                setFieldManager({ open: false, category: null, fields: [], saving: false });
                fieldForm.resetFields();
                message.success("字段配置已保存");
            } catch (saveError) {
                console.error("保存 operationControl 字段配置失败", saveError);
                setFieldManager((current) => ({ ...current, saving: false }));
                message.error("字段配置保存失败");
            }
        },
        [fieldConfig, fieldForm, fieldManager],
    );

    const renderBlockHeaderActions = useCallback(
        (block) => {
            const category = block.category || block.transactionType;
            if (!category) return null;

            return (
                <span
                    className="operation-control-block-header-action-wrap"
                    data-grid-core-ignore-sort
                    onMouseDown={(event) => event.stopPropagation()}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Dropdown
                        trigger={["click"]}
                        menu={{
                            items: [{ key: "manageFields", label: "管理字段" }],
                            onClick: ({ key, domEvent }) => {
                                domEvent?.stopPropagation?.();
                                if (key === "manageFields") openFieldManager(block);
                            },
                        }}
                    >
                        <Button
                            type="text"
                            size="small"
                            className="operation-control-block-header-action"
                            icon={<PlusOutlined />}
                            title={`管理 ${block.label} 字段`}
                            aria-label={`管理 ${block.label} 字段`}
                        />
                    </Dropdown>
                </span>
            );
        },
        [openFieldManager],
    );

    const { columns, visibleBlocks } = useColumnBlocks({
        frozenColumns: leadingColumns,
        columnBlocks,
        renderCell: renderBlockCell,
        onBlockHeaderClick: handleBlockHeaderClick,
        renderBlockHeaderActions,
    });
    const gridColumnState = useGridColumnState({
        initialColumns: columns,
        persistence: columnWidthPreferenceAdapter,
        constraints: OPERATION_GRID_COLUMN_WIDTH_CONSTRAINTS,
    });

    const leafColumns = useMemo(() => flattenLeafColumns(columns), [columns]);
    gridShapeRef.current = { rows, columns: leafColumns };

    useEffect(() => {
        const focus = selection.focus;
        if (!focus) return;

        const row = rows[focus.rowIdx];
        const column = leafColumns[focus.colIdx];
        if (!row || !column || !isNavigationColumn(column)) {
            clearSelection();
            return;
        }

        const focusCategory = focus.category || focus.transactionType;
        const columnCategory = column.category || column.transactionType;
        const categoryChanged = Boolean(focusCategory && focusCategory !== columnCategory);
        const fieldChanged = Boolean(focus.fieldKey && focus.fieldKey !== column.fieldKey);
        if ((focus.rowId && focus.rowId !== row.row_id) || (focus.columnKey && focus.columnKey !== column.key) || categoryChanged || fieldChanged) {
            clearSelection();
        }
    }, [clearSelection, leafColumns, rows, selection.focus]);

    const handleCommit = useCallback(
        (rowIdx, colIdx, value) => {
            const row = rows[rowIdx];
            const column = leafColumns[colIdx];
            if (!canEditItems || editBufferSubmitting || !row || !isEditableItemColumn(column)) return { status: "noop" };

            const category = column.category || column.transactionType;
            const { fieldKey } = column;
            const legId = row.leg_id ?? row.raw?.leg_id;
            if (legId === null || legId === undefined) {
                message.error("缺少航段 ID，无法缓存事项编辑");
                return { status: "failure" };
            }

            const currentItem = getPrimaryOperationItem(row.items, category);
            const nextCellKey = buildOperationCellKey(legId, category, fieldKey);
            const serverValue = readOperationItemField(currentItem, fieldKey);
            const previousValue = editBufferStore.getCellValue(nextCellKey, serverValue);
            const nextValue = normalizeCommitValue(column, value);
            if (areCellValuesEqual(previousValue, nextValue)) return { status: "noop" };

            editBufferStore.applyEdit(nextCellKey, nextValue, previousValue);
            setFocus(rowIdx, colIdx, {
                rowId: row.row_id,
                columnKey: column.key,
                row,
                category,
                transactionType: category,
                blockId: column.blockId,
                fieldKey,
                itemId: currentItem?.id,
                lockVersion: currentItem?.lock_version,
                cellKey: nextCellKey,
                value: nextValue,
            });

            return { status: "buffered", cellKey: nextCellKey };
        },
        [canEditItems, editBufferStore, editBufferSubmitting, leafColumns, rows, setFocus],
    );

    const handleSubmitPendingEdits = useCallback(
        async () => {
            if (editBufferSubmitting) return;

            const pendingEdits = editBufferStore.getPendingEdits();
            if (!pendingEdits.size) return;

            const batchGroups = buildEditBufferBatchGroups(pendingEdits, payload.items);
            if (!batchGroups.length) {
                message.error("没有可提交的有效编辑");
                return;
            }

            const submittingCellKeys = batchGroups.flatMap((group) => group.cellKeys);
            editBufferStore.markSubmitting(submittingCellKeys);
            setEditBufferSubmitting(true);
            setEditBufferSubmittingCount(submittingCellKeys.length);

            try {
                const response = await operationApi.batchUpdateItems({
                    operations: batchGroups.map((group) => group.operation),
                });
                const serverResults = Array.isArray(response?.results) ? response.results : [];
                const mappedResults = mapBatchSubmitResults(batchGroups, serverResults);

                setPayload((currentPayload) => applyBatchSuccessResultsToPayload(currentPayload, batchGroups, serverResults));
                const { successCount, errorCount } = editBufferStore.applySubmitResults(mappedResults);

                if (successCount > 0 && errorCount > 0) {
                    message.warning(`已提交 ${successCount} 项，${errorCount} 项失败，请检查红色格子`);
                } else if (successCount > 0) {
                    message.success(`已提交 ${successCount} 项编辑`);
                } else if (errorCount > 0) {
                    message.error(`${errorCount} 项编辑提交失败，请检查红色格子`);
                }
            } catch (submitError) {
                console.error("批量提交 operation item 失败", submitError);
                const errorMessage = getSubmitFailureMessage(submitError);
                editBufferStore.applySubmitResults(submittingCellKeys.map((cellKey) => ({
                    cellKey,
                    success: false,
                    errorCode: "NETWORK_ERROR",
                    errorMessage,
                })));
                message.error(errorMessage);
            } finally {
                setEditBufferSubmitting(false);
                setEditBufferSubmittingCount(0);
            }
        },
        [editBufferStore, editBufferSubmitting, payload.items],
    );

    const handleDiscardAllEdits = useCallback(
        () => {
            const pendingCount = editBufferStore.getPendingEdits().size;
            if (!pendingCount || editBufferSubmitting) return;

            Modal.confirm({
                title: "撤销所有编辑",
                content: `确定放弃全部 ${pendingCount} 项待提交编辑？`,
                okText: "放弃编辑",
                okButtonProps: { danger: true },
                cancelText: "取消",
                onOk: () => editBufferStore.discardAll(),
            });
        },
        [editBufferStore, editBufferSubmitting],
    );

    useEffect(() => {
        function isEditorEventTarget(target) {
            if (!(target instanceof Element)) return false;
            return Boolean(target.closest(".rdg-editor-container, input, textarea, [contenteditable='true'], .ant-select-selector, .ant-select-dropdown"));
        }

        function handleEditBufferKeyDown(event) {
            if (editBufferSubmitting || event.altKey || (!event.ctrlKey && !event.metaKey)) return;
            if (isEditorEventTarget(event.target)) return;

            const key = String(event.key || "").toLowerCase();
            if (key === "z" && event.shiftKey) {
                event.preventDefault();
                if (!editBufferStore.redo()) message.info("无可重做操作");
                return;
            }

            if (key === "z") {
                event.preventDefault();
                if (!editBufferStore.undo()) message.info("无可撤销操作");
                return;
            }

            if (key === "y") {
                event.preventDefault();
                if (!editBufferStore.redo()) message.info("无可重做操作");
            }
        }

        window.addEventListener("keydown", handleEditBufferKeyDown);
        return () => {
            window.removeEventListener("keydown", handleEditBufferKeyDown);
        };
    }, [editBufferStore, editBufferSubmitting]);

    const handleCellClick = useCallback(
        (args, event) => {
            const rowIdx = rows.findIndex((row) => row.row_id === args.row.row_id);
            const column = args.column;
            const colIdx = getColumnIndex(column, leafColumns);
            if (rowIdx < 0 || colIdx < 0) return;

            if (column.isRowHeader) {
                event?.preventGridDefault?.();
                skipNextNativeSelectionChange();
                selectRow(rowIdx);
                return;
            }

            if (event?.shiftKey) {
                event.preventGridDefault();
                skipNextNativeSelectionChange();
                extendRange(rowIdx, colIdx);
                return;
            }

            if (event?.ctrlKey || event?.metaKey) {
                event.preventGridDefault();
                skipNextNativeSelectionChange();
                toggleCell(rowIdx, colIdx);
                return;
            }

            setFocus(rowIdx, colIdx);
        },
        [extendRange, leafColumns, rows, selectRow, setFocus, skipNextNativeSelectionChange, toggleCell],
    );

    const handleSelectedCellChange = useCallback(
        (args) => {
            if (skipNextSelectedCellChangeRef.current) {
                skipNextSelectedCellChangeRef.current = false;
                return;
            }

            const colIdx = getColumnIndex(args.column, leafColumns);
            if (colIdx < 0 || args.column?.isRowHeader) return;
            setFocus(args.rowIdx, colIdx);
        },
        [leafColumns, setFocus],
    );

    const handleCellKeyDown = useCallback(
        (args, event) => {
            if (!event.shiftKey || args.mode !== "SELECT") return;

            const colIdx = getColumnIndex(args.column, leafColumns);
            if (colIdx < 0 || !isNavigationColumn(leafColumns[colIdx])) return;

            const target = getShiftArrowTarget(args.rowIdx, colIdx, event.key, rows.length, leafColumns);
            if (!target) return;

            event.preventDefault();
            event.preventGridDefault();
            extendRange(target.rowIdx, target.colIdx);
            skipNextNativeSelectionChange();
            args.selectCell?.({ rowIdx: target.rowIdx, idx: target.colIdx });
        },
        [extendRange, leafColumns, rows.length, skipNextNativeSelectionChange],
    );

    const handleColumnHeaderClick = useCallback(
        (column) => {
            const colIdx = getColumnIndex(column, leafColumns);
            if (colIdx < 0) return;
            selectColumn(colIdx);
        },
        [leafColumns, selectColumn],
    );

    const managedFieldBlock = useMemo(
        () => (fieldManager.category ? getBlockByCategory(fieldManager.category, columnBlocks) : null),
        [columnBlocks, fieldManager.category],
    );
    const managedFieldCategoryLabel = managedFieldBlock?.label || fieldConfig[fieldManager.category]?.label || fieldManager.category || "事项";

    return (
        <div className="operation-control-page">
            <div ref={fixedAreaRef} className="operation-control-fixed-area">
                <div className="operation-control-title-strip">
                    <div>
                        <Title level={4} className="operation-control-title">Operation Control V2</Title>
                        <Text type="secondary">单架飞机聚焦工作表 · GridCore 最小核心验证</Text>
                    </div>
                </div>

                <div className="operation-control-toolbar-row">
                    <Space size={12} wrap>
                        <Text strong>飞机</Text>
                        <AircraftSelector
                            value={selectedAircraft}
                            onChange={setSelectedAircraft}
                            style={{ width: 160 }}
                            placeholder="选择飞机"
                            valueType="registration"
                            displayMode="simple"
                            allowClear={false}
                            defaultSelectFirst
                        />
                    </Space>
                    <Space size={8} wrap>
                        <Badge count={signalCounts.alert || 0} size="small">
                            <Tag color="red">Alert</Tag>
                        </Badge>
                        <Badge count={signalCounts.review || 0} size="small">
                            <Tag color="gold">Review</Tag>
                        </Badge>
                        <Badge count={signalCounts.mail || 0} size="small">
                            <Tag color="blue">Mail</Tag>
                        </Badge>
                        <Tag color="default">Legs {payload.summary?.total_legs || 0}</Tag>
                        {selectionCellCount > 0 && <Tag color="processing">{selectionTypeLabel} {selectionCellCount} 格</Tag>}
                    </Space>
                    <Space size={8} wrap>
                        <Button icon={<ReloadOutlined />} onClick={() => setReloadToken((current) => current + 1)}>
                            刷新
                        </Button>
                        <Button icon={<SyncOutlined />} disabled>
                            邮件同步
                        </Button>
                        {selectionCellCount > 0 && <Button disabled>批量操作</Button>}
                    </Space>
                </div>

                <div className="operation-control-toolbar-row">
                    <Space size={12} wrap>
                        <Text strong>时间范围</Text>
                        <Select
                            value={dateRangeKey}
                            options={dateRangeOptions.map((option) => ({ value: option.value, label: option.label }))}
                            onChange={setDateRangeKey}
                            style={{ width: 180 }}
                        />
                        <Text type="secondary">{selectedDateRange.date_from} 至 {selectedDateRange.date_to}</Text>
                    </Space>
                    <Space size={8} wrap>
                        <Tag>筛选条件占位</Tag>
                        <Tag>{visibleBlocks.length} 个列块</Tag>
                    </Space>
                    <Button icon={<SettingOutlined />} disabled>
                        列块配置
                    </Button>
                </div>

                <OperationEditBufferToolbar
                    pendingCount={editBuffer.pendingCount}
                    submitting={editBufferSubmitting}
                    submittingCount={editBufferSubmittingCount}
                    canUndo={editBuffer.canUndo}
                    canRedo={editBuffer.canRedo}
                    onSubmit={handleSubmitPendingEdits}
                    onDiscardAll={handleDiscardAllEdits}
                />

                <div className="operation-control-focus-row">
                    <Text className="operation-control-focus-label">焦点上下文</Text>
                    <Text className="operation-control-focus-text">{focusContext}</Text>
                </div>
            </div>

            <div className="operation-control-grid-area">
                {error && <Alert className="operation-control-error" type="error" message={error} showIcon />}
                <Spin spinning={loading || !fieldConfigLoaded || !gridColumnState.isLoaded || !operationSortLoaded} wrapperClassName="operation-control-grid-spin">
                    <GridShell
                        ref={gridShellRef}
                        rows={rows}
                        columns={columns}
                        rowKeyGetter={getOperationRowKey}
                        fixedAreaRef={fixedAreaRef}
                        minHeight={360}
                        bottomOffset={32}
                        editable={canEditItems && !editBufferSubmitting}
                        selection={selection}
                        columnState={gridColumnState.state}
                        columnWidthConstraints={OPERATION_GRID_COLUMN_WIDTH_CONSTRAINTS}
                        sortController={gridSort}
                        onCellClick={handleCellClick}
                        onCellKeyDown={handleCellKeyDown}
                        onSelectedCellChange={handleSelectedCellChange}
                        onColumnResize={gridColumnState.setColumnWidth}
                        onColumnHeaderClick={handleColumnHeaderClick}
                        onCommit={handleCommit}
                        noRowsFallback={<div className="operation-control-grid-empty">暂无航段数据</div>}
                    />
                </Spin>
            </div>

            <Modal
                title={`管理 ${managedFieldCategoryLabel} 字段`}
                open={fieldManager.open}
                onCancel={closeFieldManager}
                onOk={handleSaveFieldConfig}
                okText="保存"
                cancelText="取消"
                confirmLoading={fieldManager.saving}
                destroyOnClose
            >
                <div className="operation-control-field-manager">
                    <Text strong>固定字段</Text>
                    <List
                        className="operation-control-field-list"
                        size="small"
                        dataSource={FIXED_OPERATION_FIELDS}
                        renderItem={(field) => (
                            <List.Item>
                                <Space size={8} wrap>
                                    <Text code>{field.key}</Text>
                                    <Text>{field.label}</Text>
                                    <Tag color="blue">固定字段</Tag>
                                </Space>
                            </List.Item>
                        )}
                    />

                    <Divider className="operation-control-field-divider" />

                    <Text strong>自定义字段</Text>
                    {fieldManager.fields.length === 0 ? (
                        <Empty className="operation-control-field-empty" image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无自定义字段" />
                    ) : (
                        <List
                            className="operation-control-field-list"
                            size="small"
                            dataSource={fieldManager.fields}
                            rowKey="key"
                            renderItem={(field) => (
                                <List.Item
                                    actions={[
                                        <Popconfirm
                                            key="delete"
                                            title="删除字段"
                                            description={`确认删除 ${field.label || field.key} 列？`}
                                            okText="删除"
                                            cancelText="取消"
                                            onConfirm={() => handleRemoveField(field.key)}
                                        >
                                            <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                                                删除
                                            </Button>
                                        </Popconfirm>,
                                    ]}
                                >
                                    <Space size={8} wrap>
                                        <Text code>{field.key}</Text>
                                        <Text>{field.label || field.key}</Text>
                                        <Tag color="green">可编辑</Tag>
                                    </Space>
                                </List.Item>
                            )}
                        />
                    )}

                    <Divider className="operation-control-field-divider" />

                    <Text strong>添加字段</Text>
                    <Form form={fieldForm} layout="vertical" className="operation-control-field-form" onFinish={handleAddField}>
                        <Form.Item
                            name="key"
                            label="字段键"
                            rules={[
                                { required: true, message: "请输入字段键" },
                                { pattern: OPERATION_ATTRIBUTE_FIELD_KEY_PATTERN, message: "请使用英文 snake_case，例如 supplier" },
                            ]}
                        >
                            <Input placeholder="supplier" autoComplete="off" />
                        </Form.Item>
                        <Form.Item name="label" label="显示名" rules={[{ required: true, message: "请输入显示名" }]}>
                            <Input placeholder="供应商" autoComplete="off" />
                        </Form.Item>
                        <Button type="dashed" icon={<PlusOutlined />} htmlType="submit" block>
                            添加字段
                        </Button>
                    </Form>
                </div>
            </Modal>
        </div>
    );
}

export default OperationControlPage;
